import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { useCart } from "../context/CartContext";
import HoverZoom from "../components/HoverZoom";
// Kept on purpose: this file also holds global rules (.btn margin, .out-of-stock) other pages rely on
import "../styles/ProductDetail.css";

export default function ProductDetail() {
  const { id } = useParams();
  const { cart, addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justAdded, setJustAdded] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      window.scrollTo(0, 0);

      // ids are numbers; anything else cannot exist
      if (!/^\d+$/.test(id ?? "")) {
        setProduct(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (cancelled) return;
      setProduct(error ? null : data);
      setLoading(false);
    }

    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="pd">
        <p className="adm-muted">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pd">
        <div className="shop-empty">
          <h3>We couldn't find that product</h3>
          <p>It may have been removed, or the link is wrong.</p>
          <Link to="/products" className="ui-btn ui-btn--primary">
            Browse all products
          </Link>
        </div>
      </div>
    );
  }

  const price = Number(product.price);
  const stock = Number(product.stock ?? 0);
  const threshold = Number(product.low_stock_threshold ?? 5);
  const inCart = cart.find((item) => item.id === product.id)?.qty ?? 0;

  const soldOut = stock <= 0;
  const maxedOut = !soldOut && inCart >= stock;
  const lowStock = !soldOut && stock <= threshold;

  function handleAdd() {
    if (soldOut || maxedOut) return;

    addToCart({
      id: product.id,
      name: product.name,
      price,
      image_url: product.image_url,
    });

    setJustAdded(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setJustAdded(false), 1400);
  }

  let buttonLabel = "Add to cart";
  if (soldOut) buttonLabel = "Out of stock";
  else if (maxedOut) buttonLabel = "All available in cart";
  else if (justAdded) buttonLabel = "Added to cart";

  return (
    <div className="pd">
      <nav className="pd-crumbs" aria-label="Breadcrumb">
        <Link to="/products">Products</Link>
        {product.category && (
          <>
            <span aria-hidden="true">/</span>
            <Link to={`/products/${encodeURIComponent(product.category)}`}>
              {product.category}
            </Link>
          </>
        )}
        <span aria-hidden="true">/</span>
        <span>{product.name}</span>
      </nav>

      <div className="pd-card">
        <div className="pd-media">
          {product.image_url ? (
            <HoverZoom src={product.image_url} alt={product.name} />
          ) : (
            <span className="pt-placeholder" aria-hidden="true">
              {product.name?.charAt(0)}
            </span>
          )}
        </div>

        <div className="pd-info">
          {product.category && <p className="pd-cat">{product.category}</p>}
          <h1 className="pd-name">{product.name}</h1>
          <p className="pd-price">£{price.toFixed(2)}</p>

          <div>
            {soldOut && <span className="ui-badge ui-badge--out">Out of stock</span>}
            {lowStock && (
              <span className="ui-badge ui-badge--low">Low stock, only {stock} left</span>
            )}
            {!soldOut && !lowStock && <span className="ui-badge ui-badge--ok">In stock</span>}
          </div>

          {product.description && <p className="pd-desc">{product.description}</p>}

          <div className="pd-actions">
            <button
              type="button"
              className="ui-btn ui-btn--primary"
              onClick={handleAdd}
              disabled={soldOut || maxedOut}
            >
              {buttonLabel}
            </button>
            {inCart > 0 && <span className="pd-note">{inCart} in your cart</span>}
          </div>
        </div>
      </div>
    </div>
  );
}